<template>
  <div class="p-4 space-y-4 border rounded-lg bg-gray-50">
    <div>
      <label class="block text-sm font-semibold text-gray-700 mb-2">Default Marker Color</label>
      <p class="text-xs text-gray-500 mb-3">This color will be used for companies that don't have any specific field values selected below.</p>

      <!-- Hidden color picker input -->
      <input
        ref="colorPicker"
        type="color"
        v-model="selectedColor"
        @input="updateDefaultColor"
        class="opacity-0 absolute top-0 left-0 w-0 h-0 pointer-events-none"
        aria-label="Default marker color picker"
      />

      <!-- Clickable color preview -->
      <div
        class="tw-flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity p-3 bg-white rounded border"
        @click="openColorPicker"
        title="Click to change default marker color"
      >
        <div
          class="w-10 h-10 rounded border-2 border-gray-300"
          :style="{ backgroundColor: selectedColor }"
        ></div>
        <div class="flex-1">
          <p class="text-sm font-medium text-gray-700">{{ selectedColor }}</p>
          <p class="text-xs text-gray-500">Click to change</p>
        </div>
      </div>
    </div>

    <!-- Bulk Color Generation -->
    <div v-if="enabledFieldCount > 0" class="pt-3 border-t">
      <label class="block text-sm font-medium text-gray-700 mb-2">Bulk Color Assignment</label>
      <p class="text-xs text-gray-500 mb-3">
        Generate {{ enabledFieldCount }} distinct colors for all selected field values based on the default color theme.
      </p>
      <ds-button
        variant="rounded-secondary"
        size="small"
        label="Auto-Generate All Colors"
        icon="palette"
        @click="generateAllFieldColors"
        class="w-full"
      />
      <p class="text-xs text-gray-400 mt-2">
        This will override any custom colors you've set for individual field values.
      </p>
    </div>
  </div>
</template>

<script>
  import DsButton from '@/components/DsButton/DsButton.vue'

  export default {
    name: 'ColorThemeSelector',
    components: {
      DsButton
    },
    emits: ['default-color-changed', 'generate-all-colors'],
    props: {
      enabledFieldCount: {
        type: Number,
        default: 0
      },
      enabledFieldValues: {
        type: Array,
        default: () => []
      },
      defaultColor: {
        type: String,
        default: '#4f46e5'
      }
    },
    data () {
      return {
        selectedColor: this.defaultColor || '#4f46e5',
      };
    },
    watch: {
      defaultColor (newVal) {
        if (newVal) {
          this.selectedColor = newVal
        }
      }
    },
    methods: {
      openColorPicker () {
        this.$refs.colorPicker.click();
      },
      updateDefaultColor () {
        this.$emit('default-color-changed', this.selectedColor);
      },
      generateAllFieldColors () {
        // Generate distinct colors based on the selected theme color
        const hsl = this.hexToHsl(this.selectedColor);
        const colors = [];

        // Generate colors for all field values with even hue distribution
        const hueStep = 360 / Math.max(this.enabledFieldCount, 2);

        for (let i = 0; i < this.enabledFieldCount; i++) {
          const newHue = (hsl.h + (hueStep * i)) % 360;
          const color = this.hslToHex({ h: newHue, s: hsl.s, l: hsl.l });
          colors.push(color);
        }

        // Emit the generated colors to the parent
        this.$emit('generate-all-colors', colors);
      },
      hexToHsl (hex) {
        const bigint = parseInt(hex.replace('#', ''), 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;
        r /= 255;
        g /= 255;
        b /= 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0;
        let s = 0;
        let l = (max + min) / 2;

        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case r:
              h = (g - b) / d + (g < b ? 6 : 0);
              break;
            case g:
              h = (b - r) / d + 2;
              break;
            case b:
              h = (r - g) / d + 4;
              break;
          }
          h = h * 60;
        }

        return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
      },
      hslToHex ({ h, s, l }) {
        // Convert HSL to RGB first, then to hex
        const hNorm = h / 360;
        const sNorm = s / 100;
        const lNorm = l / 100;

        const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
        const x = c * (1 - Math.abs((hNorm * 6) % 2 - 1));
        const m = lNorm - c / 2;

        let r = 0, g = 0, b = 0;

        if (hNorm * 6 >= 0 && hNorm * 6 < 1) {
          r = c;
          g = x;
          b = 0;
        } else if (hNorm * 6 >= 1 && hNorm * 6 < 2) {
          r = x;
          g = c;
          b = 0;
        } else if (hNorm * 6 >= 2 && hNorm * 6 < 3) {
          r = 0;
          g = c;
          b = x;
        } else if (hNorm * 6 >= 3 && hNorm * 6 < 4) {
          r = 0;
          g = x;
          b = c;
        } else if (240 <= h && h < 300) {
          r = x;
          g = 0;
          b = c;
        } else if (300 <= h && h < 360) {
          r = c;
          g = 0;
          b = x;
        }

        r = Math.round((r + m) * 255);
        g = Math.round((g + m) * 255);
        b = Math.round((b + m) * 255);

        // Convert RGB to hex
        return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
      },
    },
  };
</script>

<style scoped>
  :root {
    --theme-color: #4f46e5;
  }

  .theme-text {
    color: var(--theme-color);
  }

  .theme-bg {
    background-color: var(--theme-color);
  }

  /* small visual tweaks */
  input[type="color"] {
    padding: 0;
    border: none;
  }
</style>
